import { useState, useEffect } from "react";
import { toast } from "react-toastify";
import { getServicesAPI } from "../../../../../utils/APIs/servicesApis";
import { AssignPhotographerToBookingAPI } from "../../../../../utils/APIs/bookingsApis";
import { axiosInstance } from "../../../../../utils/APIs/commonHeadApiLogic";

/**
 * Customize hourly float — one role accordion; per-plan packageIndex when multiple hourlyPackages.
 */
const useCustomizeHourlyFloat = ({ booking, isOpen, onClose, refetchData }) => {
    const [loadingAddBooking, setLoadingAddBooking] = useState(false);
    const [services, setServices] = useState([]);
    const [selectedIds, setSelectedIds] = useState([]);
    const [selectedDetails, setSelectedDetails] = useState([]);
    const [localBooking, setLocalBooking] = useState(booking);
    const [refreshVersion, setRefreshVersion] = useState(0);
    const [selectedPackageIndex, setSelectedPackageIndex] = useState(0);

    useEffect(() => {
        if (!isOpen) return;
        (async () => {
            try {
                const targetId =
                    booking.veroaBookingId || booking._id || booking.bookingId;
                if (!targetId) return;

                const res = await axiosInstance.get(
                    `/api/admins/bookings/${targetId}`
                );
                if (res?.data?.data) {
                    setLocalBooking(res.data.data);
                }
            } catch (err) {
                console.error("Failed to fetch booking details", err);
            }
        })();
    }, [
        isOpen,
        refreshVersion,
        booking.veroaBookingId,
        booking._id,
        booking.bookingId,
    ]);

    useEffect(() => {
        if (!isOpen) return;
        (async () => {
            try {
                const res = await getServicesAPI();
                setServices(res?.data?.data || []);
            } catch (err) {
                console.error("Failed to fetch services", err);
            }
        })();
    }, [isOpen]);

    const handleRoleSelectionChange = (_roleKey, ids, details) => {
        setSelectedIds(ids || []);
        setSelectedDetails(details || []);
    };

    useEffect(() => {
        if (!isOpen) return;
        setSelectedPackageIndex(0);
    }, [isOpen, localBooking?._id, localBooking?.veroaBookingId]);

    const resetAll = () => {
        setSelectedIds([]);
        setSelectedDetails([]);
    };

    const triggerRefresh = () => {
        setRefreshVersion((v) => v + 1);
        refetchData?.();
    };

    const handleRoleSubmit = async (roleKey, _actionType, photographerDetails) => {
        const ids = selectedIds;
        const details = photographerDetails?.length
            ? photographerDetails
            : selectedDetails;

        if (!ids?.length) {
            toast.error("Please select at least one professional");
            return;
        }

        try {
            setLoadingAddBooking(true);

            const resolvedRoleKey = roleKey || "photographer";

            const payload = {
                bookingId:
                    localBooking.veroaBookingId ||
                    localBooking._id ||
                    localBooking.bookingId,
                photographerDetails: (details || []).map((d) => ({
                    ...d,
                    roleKey: d.roleKey || resolvedRoleKey,
                    packageIndex:
                        d.packageIndex !== undefined && d.packageIndex !== null
                            ? d.packageIndex
                            : selectedPackageIndex,
                })),
                actionType: "request",
                packageIndex: selectedPackageIndex,
                roleSelections: {
                    [resolvedRoleKey]: ids,
                },
            };

            if (ids.length === 1) {
                payload.photographerId = ids[0];
            } else {
                payload.photographerIds = ids;
            }

            const response = await AssignPhotographerToBookingAPI(payload);

            if (response?.data?.success) {
                toast.success(
                    response?.data?.message || "Request sent successfully!"
                );
                triggerRefresh();
                resetAll();
            } else {
                toast.error(
                    response?.data?.message || "Failed to process action!"
                );
            }
        } catch (err) {
            console.error("Customize hourly float assign error:", err);
            toast.error("Something went wrong");
        } finally {
            setLoadingAddBooking(false);
        }
    };

    const handleFooterSendRequest = (roleKey) =>
        handleRoleSubmit(roleKey, "request", selectedDetails);

    return {
        services,
        loadingAddBooking,
        localBooking,
        refreshVersion,
        selectedIds,
        handleRoleSelectionChange,
        handleRoleSubmit,
        handleFooterSendRequest,
        resetAll,
        selectedPackageIndex,
        setSelectedPackageIndex,
    };
};

export default useCustomizeHourlyFloat;
